'use client'
import Link from "next/link";
import { useState } from "react";
import { Button } from "../ui/Button";
import Logo from "./Logo";
import type { ReactElement } from "react";
import ThemeToggle from "./ThemeToggle";

const navigationItems: NavItem[] = [
  {
    label: "Our Solution",
    href: "/product",
  },
  {
    label: "Industries",
    dropdown: [
      { label: "Finance", href: "/industry/finance" },
      { label: "Medicine", href: "/industry/medicine" },
      // { label: "Pharmaceutical", href: "/industry/pharma" },
    ],
  },
  { 
    label: "Research", 
    href: "/research",
  },{ 
    label: "News", 
    href: "/news",
  },{ 
    label: "Contact", 
    href: "mailto:contact@qognitive.io",
    isButton: true 
  },
];


interface NavItem {
  label: string;
  href?: string;
  dropdown?: { label: string; href: string }[];
  isButton?: boolean;
}

interface NavItemProps {
  item: NavItem;
  isMobile?: boolean;
  onNavClick: () => void;
}

const NavItemWithDropdown = ({ item, isMobile = false, onNavClick }: NavItemProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  
  if (isMobile) {
    return (
      <div>
        <Button variant="link" onClick={onNavClick}>
          {item.label}
        </Button>
        <div className="ml-4 flex flex-col gap-3 mt-2 items-start pl-6">
          {item.dropdown?.map((dropdownItem, idx) => (
            <Button key={idx} variant="link" size="sm" onClick={onNavClick}>
              <Link href={dropdownItem.href}>{dropdownItem.label}</Link>
            </Button>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div className="flex flex-col">
        <Button 
          variant="link" 
          className="z-10 cursor-default px-0"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {item.label}
        </Button>
        {isOpen && (
          <div className="absolute top-full left-0 bg-white dark:bg-background border border-gray-200 dark:border-black shadow-xl rounded-md py-2 w-48 z-50"
            role="menu"
          >
            {item.dropdown?.map((dropdownItem, idx) => (
              <Link 
                key={idx}
                href={dropdownItem.href} 
                className="block px-4 py-2 hover:bg-eggshell-200 dark:hover:bg-black"
                role="menuitem"
              >
                <Button variant="link" size="sm" >{dropdownItem.label}</Button>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Simple navigation item component
const NavItem = ({ item, onNavClick }: NavItemProps): ReactElement => (
  item.isButton ? (
    <Button variant='outline'>
      <Link href={item.href ?? '#'} onClick={onNavClick}>
        {item.label}
      </Link>
    </Button>
  ) : (
    <Button variant="link" className="px-0">
      <Link href={item.href ?? '#'} onClick={onNavClick}>
        {item.label}
      </Link>
    </Button>
  )
);

export default function Header(): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleNavClick = (): void => {
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed w-full bg-white/80 dark:bg-black/80 backdrop-blur-sm z-50 py-4">
      <div className="container mx-auto px-4 flex justify-between items-center">
        {/* Logo */}
        <Logo />
        
        <div className="flex items-start gap-1">
          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-6">
            {navigationItems.map((item, idx) => (
              item.dropdown ? (
                <NavItemWithDropdown key={idx} item={item} onNavClick={handleNavClick} />
              ) : (
                <NavItem key={idx} item={item} onNavClick={handleNavClick} />
              )
            ))}
            <div className="flex items-center">
              <ThemeToggle variant="condensed" direction="vertical" />
            </div>
          </nav>
          
          {/* Hamburger Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <div className="w-6 h-0.5 bg-current mb-1.5"></div>
            <div className="w-6 h-0.5 bg-current mb-1.5"></div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <nav className="md:hidden absolute top-full left-0 right-0 bg-white dark:bg-black backdrop-blur-sm py-4">
          <div className="mx-auto px-4 flex flex-col gap-4 items-start">
            {navigationItems.map((item, idx) => (
              item.dropdown ? (
                <NavItemWithDropdown key={idx} item={item} isMobile={true} onNavClick={handleNavClick} />
              ) : (
                <NavItem key={idx} item={item} onNavClick={handleNavClick} />
              )
            ))}
            <div className="flex justify-end w-full">
              <ThemeToggle variant="default" />
            </div>
          </div>
        </nav>
      )}
    </header>
  );
} 